import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { processError } from "../../../../common/utils/base_error";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { defaultProjetoRevisaoCadastroForm, ProjetoRevisaoCadastroForm, ProjetoRevisaoCadastroSchema } from "../schemas/Revisao_cadastro/RevisaoCadastroSchema";
import { ProjetoRevisaoModel } from "../../domain/models/revisao_projeto";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useProjetoRevisaoDisciplinaFilters } from "../hooks/Revisao_cadastro/filter";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { DropDown } from "../../../../common/domain/models/dropdown";

export type Disciplinas = {
  idDisciplina: number;
  descricao: string;
}

type LoadDisciplinasSubdiciplinasFuncState = {
  idProjeto?: number,
  tipo?: number | null,
  prancha?: string | null,
  revisao?: string | null,
  descricao?: string | null,

};

export const useRevisaoCadastroSolicitarViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getProjetosDropDownUseCase,
    },

    projetos: {
      createProjetoRevisaoUseCase,
      getProjetoRevisaoByIdUseCase,
      getDisciplinasSubdisciplinasByProjetoUseCase,
      downloadProjetoRevisaoArquivoUseCase,

    },

  } = useDependencyInjection();

  const location = useLocation();
  const navigate = useNavigate();
  const debouncedRef = useCreateDebounceHandler(750);

  const filters = useProjetoRevisaoDisciplinaFilters();

  const { pushErrorNotification, pushInfoNotification, pushWarnNotification, setCustomLoading } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [disciplinasCadastradas, setDisciplinasCadastradas] = useState<Disciplinas[]>([]);
  const [disciplinasNaoCadastradas, setDisciplinasNaoCadastradas] = useState<Disciplinas[]>([]);

  const [ddlProjetos, setDDlProjetos] = useState<DropDown[]>();
  const [selectedIdProjetoRevisao, setSelectedIdProjetoRevisao] = useState<number>();
  const [selectedProjetoRevisao, setSelectedProjetoRevisao] = useState<ProjetoRevisaoModel>();
  const [visualizar, setVisualizar] = useState(false);

  const hookForm = useForm<ProjetoRevisaoCadastroForm>({
    reValidateMode: "onSubmit",
    mode: "onSubmit",
    defaultValues: defaultProjetoRevisaoCadastroForm,
    resolver: zodResolver(ProjetoRevisaoCadastroSchema),

  });

  const selectedIdCentroCusto = hookForm.watch("idCentroCusto");
  const selectedIdProjeto = hookForm.watch("idProjeto");

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  //#region CARREGAMENTO 

  const loadDropdownProjetos = useCallback(async (idCentroCusto?: number) => {
    try {
      setLoading(true);
      const result = await getProjetosDropDownUseCase.execute(idCentroCusto);
      setDDlProjetos(result);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar projetos!"));
      setDDlProjetos([]);
    } finally {
      setLoading(false);
    }
  }, [
    getProjetosDropDownUseCase,
    pushErrorNotification
  ]);

  const loadProjetoRevisao = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const result = await getProjetoRevisaoByIdUseCase.execute(id);
        setSelectedProjetoRevisao(result);

        hookForm.setValue("idProjetoRevisao", result.idProjetoRevisao);
        hookForm.setValue("idProjeto", result.idProjeto);
        hookForm.setValue("idCentroCusto", result.idCentroCusto ?? 0);
        hookForm.setValue("idFuncionarioSolicitante", result.idFuncionarioSolicitante ?? 0);
        hookForm.setValue("dataSolicitacao", result.dataSolicitacao ? new Date(result.dataSolicitacao) : undefined);
        hookForm.setValue("observacoesSolicitacao", result.observacoesSolicitacao ?? undefined);
        hookForm.setValue("observacoesAprovacao", result.observacoesAprovacao ?? undefined);

        hookForm.setValue("nomeArquivo1", result.nomeArquivo1 ?? undefined);
        hookForm.setValue("arquivo1", null);
        hookForm.setValue("nomeArquivo2", result.nomeArquivo2 ?? undefined);
        hookForm.setValue("arquivo2", null);
        hookForm.setValue("disciplinasIds", result.disciplinasIds ?? undefined);

        await loadDropdownProjetos(result.idCentroCusto ?? undefined);

      } catch (e: unknown) {
        setErrorMessage(processError(e, "Falha ao carregar revisão")[0]);
      } finally {
        setLoading(false);
      }
    },
    [
      hookForm,
      getProjetoRevisaoByIdUseCase,
      loadDropdownProjetos
    ],
  );



  const loadDisciplinasSubdisciplinas = useCallback(async (funcState: LoadDisciplinasSubdiciplinasFuncState) => {

    try {
      setLoading(true);
      const filteredTipo = evaluateParamValue(funcState?.tipo, filters.tipo);
      const filteredPrancha = evaluateParamValue(funcState?.prancha, filters.prancha);
      const filteredRevisao = evaluateParamValue(funcState?.revisao, filters.revisao);
      const filteredDescricao = evaluateParamValue(funcState?.descricao, filters.descricao);

      if (!selectedIdProjeto) {
        pushWarnNotification("Atenção", "Selecione um projeto para carregar as disciplinas/subdisciplinas!");
        setDisciplinasCadastradas([]);
        setDisciplinasNaoCadastradas([]);
        return;
      }

      const data = await getDisciplinasSubdisciplinasByProjetoUseCase.execute(
        selectedIdProjeto,
        filteredTipo ?? 2,
        filteredPrancha,
        filteredRevisao,
        filteredDescricao
      );

      if (selectedProjetoRevisao) {
        const disciplinasIds = selectedProjetoRevisao.disciplinasIds ?? [];
        setDisciplinasCadastradas(data.filter(x => disciplinasIds.includes(x.idDisciplina)));
        setDisciplinasNaoCadastradas(data.filter(x => !disciplinasIds.includes(x.idDisciplina)));
      } else {
        setDisciplinasCadastradas([]);
        setDisciplinasNaoCadastradas(data);
      }

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar disciplinas/subdisciplinas!"));
    } finally {
      setLoading(false);
    }
  }, [
    selectedIdProjeto,
    selectedProjetoRevisao,
    filters,
    getDisciplinasSubdisciplinasByProjetoUseCase,
    pushErrorNotification,
    pushWarnNotification
  ]
  );

  //#endregion




  //#region PAGE START

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");
      const visualizarParam = params.get("visualizar");
      setVisualizar(visualizarParam === "true");

      const id = locationId ?? undefined;

      if (!id || id.length === 0) {
        setErrorMessage("Falha ao recuperar id do checklist!");
        return;
      }
      setSelectedIdProjetoRevisao(Number(id));
      await loadProjetoRevisao(Number(locationId));
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das páginas!")[0]);

    }
  });

  useEffect(() => {
    if (selectedIdCentroCusto) {
      (async () => {
        await loadDropdownProjetos(selectedIdCentroCusto);
      })();
    }

    if (selectedIdProjeto) {
      (async () => {
        await loadDisciplinasSubdisciplinas({ idProjeto: selectedIdProjeto, tipo: 2 });
      })();
    }

  }, [
    selectedIdProjeto,
    selectedIdCentroCusto,
    loadDropdownProjetos,
    loadDisciplinasSubdisciplinas
  ]);



  //#endregion



  //#region  CREATE


  const handleSubmitProjetoRevisao = useCallback(async (form: ProjetoRevisaoCadastroForm) => {
    try {
      setSaving(true);
      setCustomLoading(true, "Salvando revisão...");
      if (!selectedProjetoRevisao) {
        const result = await createProjetoRevisaoUseCase.execute(
          form.idProjeto,
          form.idCentroCusto,
          form.idFuncionarioSolicitante,
          form.dataSolicitacao ?? undefined,
          form.observacoesSolicitacao ?? undefined,

          form.arquivo1 ?? undefined,
          form.arquivo2 ?? undefined,
          form.disciplinasIds ?? undefined,
        );

        setSelectedIdProjetoRevisao(result);
        await loadProjetoRevisao(result);
        setVisualizar(true);

        navigate(`/Revisao_listasolicitar`);
        pushInfoNotification("Sucesso", "Salvo com sucesso");
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao salvar revisão!")[0]);
    } finally {
      setSaving(false);
      setCustomLoading(false);
    }
  }, [
    createProjetoRevisaoUseCase,
    selectedProjetoRevisao,
    setCustomLoading,
    loadProjetoRevisao,
    pushInfoNotification,
    navigate
  ]);

  //#endregion



  //#region MUDANÇA DE PROJETOS
  const handleChangeDisciplinasLista = useCallback((src: Disciplinas[], selected: Disciplinas[]) => {
    setDisciplinasNaoCadastradas(src);
    setDisciplinasCadastradas(selected);

    hookForm.setValue('disciplinasIds', selected.map(x => x.idDisciplina));
  }, [
    hookForm,
  ]);


  //#endregion




  //#region PESQUISA DE DISCIPLINAS

  const handleTipoChange = useDebouncedCallback(
    debouncedRef,
    (newValues: number | null) => {
      filters.setTipo(newValues);
      return async () => {
        try {
          setLoading(true);
          await loadDisciplinasSubdisciplinas({ tipo: newValues });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar disciplinas/subdisciplinas!"));
        } finally {
          setLoading(false);
        }
      };
    }, [
    filters,
    pushErrorNotification,
    loadDisciplinasSubdisciplinas]
  );

  const handlePranchaChange = useDebouncedCallback(
    debouncedRef,
    (newValues: string | null) => {
      filters.setPrancha(newValues);
      return async () => {
        try {
          setLoading(true);
          await loadDisciplinasSubdisciplinas({ prancha: newValues });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar disciplinas/subdisciplinas!"));
        } finally {
          setLoading(false);
        }
      };
    }, [
    filters,
    pushErrorNotification,
    loadDisciplinasSubdisciplinas]
  );


  const handleRevisaoChange = useDebouncedCallback(
    debouncedRef,
    (newValues: string | null) => {
      filters.setRevisao(newValues);
      return async () => {
        try {
          setLoading(true);
          await loadDisciplinasSubdisciplinas({ revisao: newValues });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar disciplinas/subdisciplinas!"));
        } finally {
          setLoading(false);
        }
      };
    }, [
    filters,
    pushErrorNotification,
    loadDisciplinasSubdisciplinas]
  );

  const handleDescricaoChange = useDebouncedCallback(
    debouncedRef,
    (newValues: string | null) => {
      filters.setDescricao(newValues);
      return async () => {
        try {
          setLoading(true);
          await loadDisciplinasSubdisciplinas({ descricao: newValues });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar disciplinas/subdisciplinas!"));
        } finally {
          setLoading(false);
        }
      };
    }, [
    filters,
    pushErrorNotification,
    loadDisciplinasSubdisciplinas]
  );


  const handleClearFilters = useDebouncedCallback(
    debouncedRef,
    () => {
      filters.clear();
      return async () => {
        try {
          setLoading(true);
          if (selectedIdProjeto) {
            await loadDisciplinasSubdisciplinas({ idProjeto: selectedIdProjeto, tipo: 2 });
          }
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar disciplinas/subdisciplinas!"));
        } finally {
          setLoading(false);
        }
      };
    }, [
    filters,
    selectedIdProjeto,
    pushErrorNotification,
    loadDisciplinasSubdisciplinas]
  );

  //#endregion


  //#region  ARQUIVOS

  const handleDownloadFile = useCallback(async (nroArquivo: number, nomeArquivo: string) => {
    if (selectedProjetoRevisao) {
      try {
        setCustomLoading(true);
        await downloadProjetoRevisaoArquivoUseCase
          .execute(selectedProjetoRevisao.idProjetoRevisao, nroArquivo, nomeArquivo);

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao baixar arquivo!'));
      } finally {
        setCustomLoading(false);
      }
    }
  }, [
    selectedProjetoRevisao,
    downloadProjetoRevisaoArquivoUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleDeleteFile = useCallback(() => {
    if (!saving) {
      if (window.confirm('Você deseja excluir este item?')) {
        hookForm.setValue('nomeArquivo1', null);
        hookForm.setValue('arquivo1', null);
        setSelectedProjetoRevisao({
          ...selectedProjetoRevisao,
          idProjetoRevisao: selectedProjetoRevisao?.idProjetoRevisao ?? 0,
          idProjeto: selectedProjetoRevisao?.idProjeto ?? 0,
          idCentroCusto: selectedProjetoRevisao?.idCentroCusto ?? 0,
          arquivo1: undefined,
          nomeArquivo1: undefined,
        });
      }
    }
  }, [
    saving,
    hookForm,
    selectedProjetoRevisao,
  ]);



  //#endregion




  //#region CABEÇALHO

  const handlePageBack = useCallback(() => {
    navigate('/Revisao_listagerenciar')

  }, [navigate]);


  const handleReloadPage = () => {
    hookForm.reset();
    const params = new URLSearchParams(location.search);
    const locationId = params.get("id");
    if (locationId) loadProjetoRevisao(Number(locationId));

  };

  //#endregion


  return {
    loading,
    selectedProjetoRevisao,
    disciplinasCadastradas,
    disciplinasNaoCadastradas,
    filters,
    visualizar,
    hookForm,
    errorMessage,
    ddlProjetos,
    saving,

    handleChangeDisciplinasLista,
    handleDeleteFile,
    handleTipoChange,
    handlePranchaChange,
    handleRevisaoChange,
    handleDescricaoChange,
    handleClearFilters,
    handleSubmitProjetoRevisao: hookForm.handleSubmit(handleSubmitProjetoRevisao),
    handlePageBack,
    handleReloadPage,
    handleDownloadFile,

  };
};
